import type { ICommentInfo } from '@src/slide/domain/ui-model/element-modeles/Interface/slide-model.interface';
import axios from 'axios';
import type { ICommentData, ICommentVipInfo, ICommentWithVipInfo } from './type';

/**
 * 单次批注拉取的 size
 */
const QUERY_PAGE_SIZE = 50;

/**
 * 获取批注接口要求的公共参数
 * @returns ICommentAPICommonOptions
 */
export function getCommentsAPICommonParams() {
    return {
        global_doc_id: window.clientVars.globalPadId,
    };
}

/**
 * 格式化接口数据为前端消费的 ICommentInfo
 * @param data 接口返回的数据
 * @returns ICommentInfo
 */
export function formatComment(data: ICommentData): ICommentWithVipInfo {
    const { id, content, nick, avatar, publisher } = data;
    let vipInfo: Partial<ICommentVipInfo> = {};
    if (data.vip_info) {
        const vipInfoParsed = JSON.parse(data.vip_info);
        vipInfo = {
            vipLevel: vipInfoParsed.vip_level,
            avatarFrameUrl: vipInfoParsed.avatar_frame_url,
            badgeUrl: vipInfoParsed.badge_url,
            renewalStatus: vipInfoParsed.renewal_status,
            expiredStatus: vipInfoParsed.expired_status,
        };
    }

    return {
        id,
        uin: publisher,
        nickName: nick,
        userImg: avatar,
        content,
        times: data.update_time,
        ...vipInfo,
    };
}

/**
 * 批量查询批注
 * @param anchorIds 批注id列表
 * @returns
 */
export async function batchQueryComments(anchorIds: string[]): Promise<Map<string, ICommentWithVipInfo[]>> {
    const prepend = '300000000$';
    let fileId = '';
    if (window.clientVars.globalPadId.startsWith(prepend)) {
        fileId = window.clientVars.globalPadId.substring(prepend.length);
    }
    const response = (
        await axios({
            url: '/api/annotation/anchors',
            method: 'POST',
            data: JSON.stringify({
                anchor_ids: anchorIds,
                file_id: fileId,
            }),
            headers: {
                'content-type': 'application/json',
            },
            withCredentials: true,
        })
    ).data;

    if (parseInt(response.retcode, 10) !== 0) {
        return Promise.reject(response.msg);
    }

    const commentMap = new Map();
    Object.keys(response?.result?.anchors).forEach((groupId) => {
        const formatComments: ICommentWithVipInfo[] = [];
        const comments = response.result.anchors[groupId].comments as [] ;
        if (comments?.length) {
            formatComments.push(...comments.map(formatComment));
            commentMap.set(groupId, formatComments);
        }
    });
    return commentMap;
}

/**
 * 拉取批注数据
 * 后端提供了分页拉取数据的版本，这里前端轮询拉取全量数据（较为理想的方案是后期前端改为滚动按需加载）
 * @param anchorId 锚点 ID
 */
export async function getComments(anchorId: string, slideId: string): Promise<ICommentInfo[]> {
    let currentPageNum = 1;
    let hasDone = false;

    const result: ICommentInfo[] = [];
    while (hasDone === false) {
        const response = (
            await axios({
                url: '/api/annotation/comments',
                method: 'POST',
                data: JSON.stringify({
                    page_num: currentPageNum,
                    page_size: QUERY_PAGE_SIZE,
                    anchor_id: anchorId,
                    sheet_id: slideId,
                    ...getCommentsAPICommonParams(),
                }),
                headers: {
                    'content-type': 'application/json',
                },
                withCredentials: true,
            })
        ).data;
        if (parseInt(response.retcode, 10) !== 0) {
            return Promise.reject(response.msg);
        }
        // 更新是否完成
        hasDone = response.result?.done;
        // 更新下一页的页码
        currentPageNum += 1;

        const comments = response?.result?.anchor?.comments as [];

        if (comments?.length) {
            result.push(...comments.map(formatComment));
        }
    }
    return result;
}

/**
 * 拉取批注数据
 * @param anchorId 锚点 ID
 */
export async function addComment(anchorId: string, slideId: string, content: string) {
    const response = (
        await axios({
            url: '/api/annotation/add',
            method: 'POST',
            data: JSON.stringify({
                anchor_id: anchorId,
                sheet_id: slideId,
                content,
                ...getCommentsAPICommonParams(),
            }),
            headers: {
                'content-type': 'application/json',
            },
            withCredentials: true,
        })
    ).data;

    if (parseInt(response.retcode, 10) !== 0) {
        return Promise.reject(response.msg);
    }

    return formatComment(response?.result?.comment);
}

/**
 * 更新批注数据
 * @param anchorId 锚点 ID
 * @param anchorId 批注 ID
 * @param content 批注内容
 */
export async function updateComment(
    commentId: string, anchorId: string, slideId: string,
    content: string,
): Promise<ICommentInfo> {
    const response = (
        await axios({
            url: '/api/annotation/update',
            method: 'POST',
            data: JSON.stringify({
                anchor_id: anchorId,
                comment_id: commentId,
                sheet_id: slideId,
                content,
                ...getCommentsAPICommonParams(),
            }),
            headers: {
                'content-type': 'application/json',
            },
            withCredentials: true,
        })
    ).data;

    if (parseInt(response.retcode, 10) !== 0) {
        return Promise.reject(response.msg);
    }

    return formatComment(response?.result?.comment);
}

/**
 * 删除批注
 */
export async function deleteComment(anchorId: string, commentId: string, slideId: string) {
    const response = (
        await axios({
            url: '/api/annotation/del',
            method: 'POST',
            data: JSON.stringify({
                anchor_id: anchorId,
                comment_id: commentId,
                sheet_id: slideId,
                ...getCommentsAPICommonParams(),
            }),
            headers: {
                'content-type': 'application/json',
            },
            withCredentials: true,
        })
    ).data;

    if (parseInt(response.retcode, 10) !== 0) {
        return false;
    }

    return true;
}

/**
 * 删除当前锚点的全部批注
 */
export async function deleteCommentGroup(anchorId: string, slideId: string) {
    const response = (
        await axios({
            url: '/api/annotation/clear',
            method: 'POST',
            data: JSON.stringify({
                anchor_id: anchorId,
                sheet_id: slideId,
                ...getCommentsAPICommonParams(),
            }),
            headers: {
                'content-type': 'application/json',
            },
            withCredentials: true,
        })
    ).data;

    if (parseInt(response.retcode, 10) !== 0) {
        return false;
    }

    return true;
}
